import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  ProviderType,
  UniversalBookingLocation,
  CreateLocationInput,
} from "@/types/booking";

export function useUniversalLocations(
  providerType: ProviderType | undefined,
  providerId: string | undefined
) {
  const [locations, setLocations] = useState<UniversalBookingLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLocations = useCallback(async () => {
    if (!providerType || !providerId) {
      setLocations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("universal_booking_locations")
        .select("*")
        .eq("provider_type", providerType)
        .eq("provider_id", providerId)
        .order("is_primary", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching locations:", error);
        toast.error("Error al cargar ubicaciones");
        return;
      }

      setLocations((data || []) as UniversalBookingLocation[]);
    } catch (err) {
      console.error("Error in fetchLocations:", err);
    } finally {
      setIsLoading(false);
    }
  }, [providerType, providerId]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const createLocation = async (
    input: Omit<CreateLocationInput, "provider_type" | "provider_id">
  ): Promise<UniversalBookingLocation | null> => {
    if (!providerType || !providerId) {
      toast.error("Información del proveedor no disponible");
      return null;
    }

    try {
      // If this is the first location or marked as primary, set as primary
      const isPrimary = input.is_primary || locations.length === 0;

      // If setting as primary, unset other primary locations
      if (isPrimary && locations.length > 0) {
        await supabase
          .from("universal_booking_locations")
          .update({ is_primary: false })
          .eq("provider_type", providerType)
          .eq("provider_id", providerId);
      }

      const { data, error } = await supabase
        .from("universal_booking_locations")
        .insert({
          provider_type: providerType,
          provider_id: providerId,
          name: input.name,
          address: input.address || null,
          city: input.city || null,
          is_primary: isPrimary,
          is_active: true,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating location:", error);
        toast.error("Error al crear ubicación");
        return null;
      }

      toast.success("Ubicación añadida");
      await fetchLocations();
      return data as UniversalBookingLocation;
    } catch (err) {
      console.error("Error in createLocation:", err);
      toast.error("Error inesperado");
      return null;
    }
  };

  const updateLocation = async (
    locationId: string,
    updates: Partial<UniversalBookingLocation>
  ): Promise<boolean> => {
    try {
      // If setting as primary, unset other primary locations
      if (updates.is_primary && providerType && providerId) {
        await supabase
          .from("universal_booking_locations")
          .update({ is_primary: false })
          .eq("provider_type", providerType)
          .eq("provider_id", providerId)
          .neq("id", locationId);
      }

      const { error } = await supabase
        .from("universal_booking_locations")
        .update(updates)
        .eq("id", locationId);

      if (error) {
        console.error("Error updating location:", error);
        toast.error("Error al actualizar ubicación");
        return false;
      }

      toast.success("Ubicación actualizada");
      await fetchLocations();
      return true;
    } catch (err) {
      console.error("Error in updateLocation:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const deleteLocation = async (locationId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("universal_booking_locations")
        .delete()
        .eq("id", locationId);

      if (error) {
        console.error("Error deleting location:", error);
        toast.error("Error al eliminar ubicación");
        return false;
      }

      toast.success("Ubicación eliminada");
      await fetchLocations();
      return true;
    } catch (err) {
      console.error("Error in deleteLocation:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const setPrimaryLocation = async (locationId: string): Promise<boolean> => {
    return updateLocation(locationId, { is_primary: true });
  };

  return {
    locations,
    activeLocations: locations.filter((l) => l.is_active),
    primaryLocation: locations.find((l) => l.is_primary),
    isLoading,
    createLocation,
    updateLocation,
    deleteLocation,
    setPrimaryLocation,
    refetch: fetchLocations,
  };
}
